import { useState, useEffect } from 'react';
import { useParams, useNavigate } from 'react-router-dom';
import { toast } from 'react-toastify';
import adminApiService from '../../services/adminApiService';

const AdminManualPaymentDetails = () => {
  const { ticketId } = useParams();
  const navigate = useNavigate();
  const [loading, setLoading] = useState(true);
  const [paymentDetails, setPaymentDetails] = useState(null);
  const [updatingStatus, setUpdatingStatus] = useState(false);

  useEffect(() => {
    if (ticketId) {
      loadPaymentDetails();
    }
  }, [ticketId]);

  const loadPaymentDetails = async () => {
    try {
      setLoading(true);
      console.log('💳 Loading manual payment details for Ticket ID:', ticketId);
      const response = await adminApiService.getManualPaymentByTicketId(ticketId);
      console.log('📊 Manual Payment Details API Response:', response);
      
      if (response.success) {
        setPaymentDetails(response.data);
        console.log('✅ Manual payment details loaded successfully');
      } else {
        console.error('❌ Manual Payment Details API returned error:', response.message);
        toast.error(response.message || 'Failed to load payment details');
        navigate('/admin/dashboard/manual-payment-info');
      }
    } catch (error) {
      console.error('❌ Manual payment details load error:', error);
      toast.error('Failed to load payment details');
      navigate('/admin/dashboard/manual-payment-info');
    } finally {
      setLoading(false);
    }
  };

  const handleStatusUpdate = async () => {
    if (!paymentDetails) return;
    
    try {
      setUpdatingStatus(true);
      
      const response = await adminApiService.updateTransactionStatus(
        paymentDetails.agentId,
        paymentDetails.transactionId,
        paymentDetails.amount,
        paymentDetails.status
      );
      
      if (response.success) {
        toast.success('Transaction status updated successfully!');
        // Reload details to get fresh data
        await loadPaymentDetails();
      } else {
        toast.error(response.message || 'Failed to update transaction status');
      }
    } catch (err) {
      console.error('Error updating transaction status:', err);
      toast.error('Failed to update transaction status. Please try again.');
    } finally {
      setUpdatingStatus(false);
    }
  };

  const handleStatusChange = (newStatus) => {
    setPaymentDetails(prev => ({ ...prev, status: newStatus }));
  };

  const formatCurrency = (amount) => {
    if (!amount) return '-';
    try {
      return new Intl.NumberFormat('en-IN', {
        style: 'currency',
        currency: 'INR'
      }).format(amount);
    } catch (error) {
      return `₹${amount}`;
    }
  };

  const formatDate = (dateString) => {
    if (!dateString) return '-';
    try {
      return new Date(dateString).toLocaleDateString('en-US', {
        year: 'numeric',
        month: 'short',
        day: 'numeric',
        hour: '2-digit',
        minute: '2-digit'
      });
    } catch (error) {
      return dateString;
    }
  };

  const getStatusBadge = (status) => {
    const statusColors = {
      'VERIFIED': 'bg-green-100 text-green-800',
      'PENDING': 'bg-yellow-100 text-yellow-800',
      'REJECTED': 'bg-red-100 text-red-800'
    };

    const colorClass = statusColors[status?.toUpperCase()] || 'bg-gray-100 text-gray-800';

    return (
      <span className={`inline-flex items-center px-2.5 py-0.5 rounded-full text-xs font-medium ${colorClass}`}>
        {status || 'PENDING'}
      </span>
    );
  };

  if (loading) {
    return (
      <div className="flex items-center justify-center h-64">
        <div className="animate-spin rounded-full h-12 w-12 border-b-2 border-red-600"></div>
      </div>
    );
  }

  if (!paymentDetails) {
    return (
      <div className="min-h-screen bg-gray-50 flex items-center justify-center">
        <div className="text-center">
          <h2 className="text-2xl font-bold text-gray-900 mb-4">Payment Details Not Found</h2>
          <p className="text-gray-600 mb-4">The requested payment details could not be found.</p>
          <button
            onClick={() => navigate('/admin/dashboard/manual-payment-info')}
            className="bg-red-600 hover:bg-red-700 text-white px-4 py-2 rounded-md"
          >
            Back to Manual Payments
          </button>
        </div>
      </div>
    );
  }

  return (
    <div className="min-h-screen bg-gray-50">
      <div className="container mx-auto px-4 py-6 max-w-6xl">
        {/* Header */}
        <div className="mb-6">
          <div className="flex items-center justify-between">
            <div>
              <h1 className="text-2xl font-bold text-gray-900">Manual Payment Details</h1>
              <p className="text-gray-600">View and manage manual payment transaction details</p>
            </div>
            <button
              onClick={() => navigate('/admin/dashboard/manual-payment-info')}
              className="bg-gray-600 hover:bg-gray-700 text-white px-4 py-2 rounded-md text-sm font-medium transition-colors"
            >
              Back to Manual Payments
            </button>
          </div>
        </div>

        <div className="grid grid-cols-1 lg:grid-cols-3 gap-6">
          {/* Main Payment Details */}
          <div className="lg:col-span-2">
            <div className="bg-white rounded-lg shadow-sm border border-gray-200">
              <div className="px-6 py-4 border-b border-gray-200">
                <h3 className="text-lg font-semibold text-gray-900">Transaction Information</h3>
              </div>
              
              <div className="p-6">
                <div className="grid grid-cols-1 md:grid-cols-2 gap-6">
                  {/* Transaction Details */}
                  <div>
                    <label className="block text-sm font-medium text-gray-700 mb-1">Ticket ID</label>
                    <input
                      type="text"
                      value={paymentDetails.id || '-'}
                      readOnly
                      className="w-full px-3 py-2 border border-gray-300 rounded-md bg-gray-50 text-gray-900"
                    />
                  </div>

                  <div>
                    <label className="block text-sm font-medium text-gray-700 mb-1">Transaction ID</label>
                    <input
                      type="text"
                      value={paymentDetails.transactionId || '-'}
                      readOnly
                      className="w-full px-3 py-2 border border-gray-300 rounded-md bg-gray-50 text-gray-900"
                    />
                  </div>

                  <div>
                    <label className="block text-sm font-medium text-gray-700 mb-1">Agent ID</label>
                    <input
                      type="text"
                      value={paymentDetails.agentId || '-'}
                      readOnly
                      className="w-full px-3 py-2 border border-gray-300 rounded-md bg-gray-50 text-gray-900"
                    />
                  </div>

                  <div>
                    <label className="block text-sm font-medium text-gray-700 mb-1">Amount</label>
                    <input
                      type="text"
                      value={formatCurrency(paymentDetails.amount)}
                      readOnly
                      className="w-full px-3 py-2 border border-gray-300 rounded-md bg-gray-50 text-gray-900"
                    />
                  </div>

                  {/* Bank Details */}
                  <div>
                    <label className="block text-sm font-medium text-gray-700 mb-1">Bank Name</label>
                    <input
                      type="text"
                      value={paymentDetails.bankName || '-'}
                      readOnly
                      className="w-full px-3 py-2 border border-gray-300 rounded-md bg-gray-50 text-gray-900"
                    />
                  </div>

                  <div>
                    <label className="block text-sm font-medium text-gray-700 mb-1">Branch</label>
                    <input
                      type="text"
                      value={paymentDetails.branch || paymentDetails.bankName || '-'}
                      readOnly
                      className="w-full px-3 py-2 border border-gray-300 rounded-md bg-gray-50 text-gray-900"
                    />
                  </div>

                  {/* Dates */}
                  <div>
                    <label className="block text-sm font-medium text-gray-700 mb-1">Transaction Date</label>
                    <input
                      type="text"
                      value={formatDate(paymentDetails.transactionDate)}
                      readOnly
                      className="w-full px-3 py-2 border border-gray-300 rounded-md bg-gray-50 text-gray-900"
                    />
                  </div>


                  {/* Additional Details */}
                  <div>
                    <label className="block text-sm font-medium text-gray-700 mb-1">Agent Name</label>
                    <input
                      type="text"
                      value={paymentDetails.agentName || '-'}
                      readOnly
                      className="w-full px-3 py-2 border border-gray-300 rounded-md bg-gray-50 text-gray-900"
                    />
                  </div>

                  <div>
                    <label className="block text-sm font-medium text-gray-700 mb-1">Agent Email</label>
                    <input
                      type="text"
                      value={paymentDetails.agentEmail || '-'}
                      readOnly
                      className="w-full px-3 py-2 border border-gray-300 rounded-md bg-gray-50 text-gray-900"
                    />
                  </div>
                </div>

                {/* Transaction Slip */}
                <div className="mt-6">
                  <label className="block text-sm font-medium text-gray-700 mb-1">Transaction Slip</label>
                  <div className="border border-gray-300 rounded-md p-4 bg-gray-50">
                    <button className="text-blue-600 hover:text-blue-900 font-medium">
                      <i className="fas fa-file-image mr-2"></i>
                      View Transaction Slip
                    </button>
                  </div>
                </div>
              </div>
            </div>
          </div>

          {/* Sidebar */}
          <div className="space-y-6">
            {/* Status Card */}
            <div className="bg-white rounded-lg shadow-sm border border-gray-200">
              <div className="px-6 py-4 border-b border-gray-200">
                <h3 className="text-lg font-semibold text-gray-900">Current Status</h3>
              </div>
              <div className="p-6">
                <div className="flex justify-center mb-4">
                  {getStatusBadge(paymentDetails.status)}
                </div>
              </div>
            </div>

            {/* Status Update Card */}
            <div className="bg-white rounded-lg shadow-sm border border-gray-200">
              <div className="px-6 py-4 border-b border-gray-200">
                <h3 className="text-lg font-semibold text-gray-900">Update Status</h3>
              </div>
              <div className="p-6">
                <div className="mb-4">
                  <label className="block text-sm font-medium text-gray-700 mb-2">Verified Status</label>
                  <select
                    value={paymentDetails.status || 'Pending'}
                    onChange={(e) => handleStatusChange(e.target.value)}
                    disabled={paymentDetails.status?.toUpperCase() === 'VERIFIED'}
                    className="w-full border border-gray-300 rounded-md px-3 py-2 focus:outline-none focus:ring-1 focus:ring-red-500 focus:border-red-500 disabled:bg-gray-100 disabled:cursor-not-allowed"
                  >
                    <option value="Pending">Pending</option>
                    <option value="Verified">Verified</option>
                    <option value="Rejected">Rejected</option>
                  </select>
                </div>
                <button
                  onClick={handleStatusUpdate}
                  disabled={updatingStatus}
                  className="w-full bg-red-600 hover:bg-red-700 text-white px-4 py-2 rounded-md text-sm font-medium transition-colors disabled:opacity-50 disabled:cursor-not-allowed"
                >
                  {updatingStatus ? (
                    <div className="flex items-center justify-center">
                      <div className="animate-spin rounded-full h-4 w-4 border-b-2 border-white mr-2"></div>
                      Updating Status...
                    </div>
                  ) : (
                    'Update Status'
                  )}
                </button>
              </div>
            </div>

            {/* Quick Info Card */}
            <div className="bg-white rounded-lg shadow-sm border border-gray-200">
              <div className="px-6 py-4 border-b border-gray-200">
                <h3 className="text-lg font-semibold text-gray-900">Quick Info</h3>
              </div>
              <div className="p-6 space-y-4">
                <div className="flex justify-between">
                  <span className="text-sm text-gray-600">Payment Method</span>
                  <span className="text-sm font-medium text-gray-900">Manual Transfer</span>
                </div>
                <div className="flex justify-between">
                  <span className="text-sm text-gray-600">Currency</span>
                  <span className="text-sm font-medium text-gray-900">INR</span>
                </div>
                <div className="flex justify-between">
                  <span className="text-sm text-gray-600">Processing Time</span>
                  <span className="text-sm font-medium text-gray-900">1-2 Business Days</span>
                </div>
              </div>
            </div>
          </div>
        </div>
      </div>
    </div>
  );
};

export default AdminManualPaymentDetails;